import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

type Poll = { id: string; question: string; options: string[]; multiple: boolean; closesAt: string | null; open: boolean; audience: string; myChoices: number[] | null; voters: number; canVote: boolean; canClose: boolean; results: number[] | null };

/** Encuestas: votar y ver resultados; Pastoral pregunta a la iglesia, el líder a su ministerio. */
export function PollsPage() {
  const me = useMe();
  const base = `/communication/churches/${me.activeChurchId}/polls`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ isPastoral: boolean; items: Poll[] }>(base, signal) : Promise.resolve(null)), [base]);
  const ministries = useAsync((signal) => (me.activeChurchId ? api.get<{ items: { id: string; name: string; leader?: { personId: string } | null; coLeaders?: { personId: string }[] }[] }>(`/organization/churches/${me.activeChurchId}/ministries`, signal).then((r) => r.items) : Promise.resolve([])), [me.activeChurchId]);
  // «Encuesta al ministerio» (desde la ficha del ministerio): llega con ?ministerio=… y abre el formulario con él elegido.
  const preset = useSearchParams()[0].get('ministerio');
  const [open, setOpen] = useState(Boolean(preset));
  const [options, setOptions] = useState(['', '']);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<Record<string, number[]>>({});
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  const led = ministries.status === 'ready' ? ministries.data.filter((m) => m.leader?.personId === me.personId || m.coLeaders?.some((c) => c.personId === me.personId)) : [];
  const canCreate = data.status === 'ready' && data.data && (data.data.isPastoral || led.length > 0);
  const run = async (action: () => Promise<unknown>, ok: string | null) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      if (ok) setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Encuestas</h1>
        <p className="muted">Tu voto es anónimo; los resultados se ven al votar.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {canCreate && !open ? <div><Button onClick={() => setOpen(true)}>Nueva encuesta</Button></div> : null}
      {open ? (
        <Card title="Nueva encuesta">
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const closes = String(f.get('closes') ?? '');
              void run(() => api.post(base, {
                question: String(f.get('question') ?? '').trim(), options: options.map((o) => o.trim()).filter(Boolean), multiple: f.get('multiple') === 'on',
                ministryId: String(f.get('audience') ?? '') || null, closesAt: closes ? new Date(closes).toISOString() : null,
              }), 'Encuesta enviada.').then((ok) => { if (ok) { setOpen(false); setOptions(['', '']); } });
            }}
          >
            <Field label="Pregunta" name="question" required maxLength={200} placeholder="¿Qué día podemos ensayar?" />
            {options.map((o, i) => (
              <Field key={i} label={`Opción ${i + 1}`} value={o} maxLength={100} onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))} />
            ))}
            {options.length < 10 ? <div><Button variant="ghost" onClick={() => setOptions([...options, ''])}>Agregar opción</Button></div> : null}
            <label className="check"><input type="checkbox" name="multiple" /> Se pueden elegir varias</label>
            <div className="form__row">
              <div className="field">
                <label htmlFor="poll-aud">Para</label>
                <select id="poll-aud" name="audience" key={ministries.status} defaultValue={preset && (data.status === 'ready' && data.data?.isPastoral ? ministries.status === 'ready' && ministries.data.some((m) => m.id === preset) : led.some((m) => m.id === preset)) ? preset : data.status === 'ready' && data.data?.isPastoral ? '' : led[0]?.id}>
                  {data.status === 'ready' && data.data?.isPastoral ? <option value="">Toda la iglesia</option> : null}
                  {(data.status === 'ready' && data.data?.isPastoral ? ministries.status === 'ready' ? ministries.data : [] : led).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <Field label="Cierra (opcional)" name="closes" type="datetime-local" />
            </div>
            <div className="actions"><Button type="submit" busy={busy}>Enviar</Button><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button></div>
          </form>
        </Card>
      ) : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data && data.data.items.length === 0 ? <Card><EmptyState title="No hay encuestas." /></Card> : null}
      {data.status === 'ready' && data.data
        ? data.data.items.map((p) => {
            const total = p.results ? p.results.reduce((a, b) => a + b, 0) : 0;
            const choice = picked[p.id] ?? p.myChoices ?? [];
            return (
              <Card key={p.id} title={p.question} action={<span className="small muted">{p.audience}{p.open ? '' : ' · cerrada'}</span>}>
                {p.canVote && p.myChoices === null ? (
                  <form className="form" onSubmit={(e) => { e.preventDefault(); void run(() => api.put(`${base}/${p.id}/vote`, { choices: choice }), 'Gracias por votar.'); }}>
                    <fieldset className="choices">
                      <legend className="small muted">{p.multiple ? 'Elige una o varias' : 'Elige una'}</legend>
                      {p.options.map((o, i) => (
                        <label key={i} className="choice">
                          <input
                            type={p.multiple ? 'checkbox' : 'radio'}
                            name={`poll-${p.id}`}
                            checked={choice.includes(i)}
                            onChange={(e) => setPicked({ ...picked, [p.id]: p.multiple ? (e.target.checked ? [...choice, i] : choice.filter((x) => x !== i)) : [i] })}
                          />
                          <span>{o}</span>
                        </label>
                      ))}
                    </fieldset>
                    <Button type="submit" busy={busy} disabled={choice.length === 0}>Votar</Button>
                  </form>
                ) : null}
                {p.results ? (
                  <ul className="poll-results">
                    {p.options.map((o, i) => {
                      const pct = total ? Math.round((p.results![i]! / total) * 100) : 0;
                      return (
                        <li key={i} className={p.myChoices?.includes(i) ? 'poll-results__mine' : ''}>
                          <span>{o} · {p.results![i]} ({pct}%)</span>
                          <progress max={100} value={pct} aria-label={o} />
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                <p className="small muted">{p.voters} {p.voters === 1 ? 'voto' : 'votos'}{p.closesAt && p.open ? ` · cierra ${new Date(p.closesAt).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}</p>
                {p.canClose ? <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Cerrar esta encuesta? Ya nadie podrá votar.')) void run(() => api.post(`${base}/${p.id}/close`), 'Encuesta cerrada.'); }}>Cerrar encuesta</Button> : null}
              </Card>
            );
          })
        : null}
    </div>
  );
}
